import axios from 'axios';
import { GroceryItem, Meal, DashboardStats, ApiResponse, DatabaseBackup, ImportResult, User, Family, GroceryList } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      window.dispatchEvent(new Event('auth:logout'));
    }
    console.error(`API Error: ${error.response?.status} ${error.config?.url}`, error.response?.data);
    return Promise.reject(error);
  }
);

// Grocery API — all reads/writes are scoped to a list
export const groceryAPI = {
  // Get all grocery items in a list
  getAll: async (listId: string, params?: { category?: string; search?: string; bought?: boolean }) => {
    const response = await api.get<ApiResponse<GroceryItem[]>>('/groceries', { params: { list: listId, ...params } });
    return response.data;
  },

  // Get single grocery item
  getById: async (id: string) => {
    const response = await api.get<ApiResponse<GroceryItem>>(`/groceries/${id}`);
    return response.data;
  },

  // Create new grocery item in a list
  create: async (listId: string, item: Omit<GroceryItem, '_id' | 'createdAt' | 'updatedAt' | 'list'>) => {
    const response = await api.post<ApiResponse<GroceryItem>>('/groceries', { list: listId, ...item });
    return response.data;
  },

  // Update grocery item
  update: async (id: string, updates: Partial<GroceryItem>) => {
    const response = await api.put<ApiResponse<GroceryItem>>(`/groceries/${id}`, updates);
    return response.data;
  },

  // Delete grocery item
  delete: async (id: string) => {
    const response = await api.delete<ApiResponse<void>>(`/groceries/${id}`);
    return response.data;
  },

  // Bulk update grocery items in a list
  bulkUpdate: async (listId: string, action: 'buyAll' | 'addAllToBuy', filters?: { category?: string; search?: string; bought?: boolean }) => {
    const response = await api.put<ApiResponse<{ matchedCount: number; modifiedCount: number }>>('/groceries/bulk', {
      list: listId,
      action,
      filters
    });
    return response.data;
  },

  // Get dashboard stats for a list
  getStats: async (listId: string) => {
    const response = await api.get<ApiResponse<DashboardStats>>('/groceries/stats', { params: { list: listId } });
    return response.data;
  },
};

// Current logged-in user, read from localStorage (set at login).
export const getCurrentUser = (): User | null => {
  try {
    const raw = localStorage.getItem('auth_user');
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
};

export const logout = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_user');
  window.dispatchEvent(new Event('auth:logout'));
};

// Auth API
export const authAPI = {
  login: async (username: string, password: string) => {
    const response = await api.post<{ token: string; user: User }>('/auth/login', { username, password });
    return response.data;
  },
  me: async () => {
    const response = await api.get<{ user: User }>('/auth/me');
    return response.data;
  },
};

// Lists API (any authenticated user, scoped to their families)
export const listAPI = {
  getAll: async () => {
    const response = await api.get<ApiResponse<GroceryList[]>>('/lists');
    return response.data;
  },
  create: async (payload: { name: string; emoji?: string; family: string }) => {
    const response = await api.post<ApiResponse<GroceryList>>('/lists', payload);
    return response.data;
  },
  update: async (id: string, updates: { name?: string; emoji?: string }) => {
    const response = await api.put<ApiResponse<GroceryList>>(`/lists/${id}`, updates);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete<ApiResponse<void>>(`/lists/${id}`);
    return response.data;
  },
};

// Users API (admin only)
export const userAPI = {
  getAll: async () => {
    const response = await api.get<{ data: User[] }>('/users');
    return response.data;
  },
  create: async (payload: { username: string; password: string; role?: 'admin' | 'member' }) => {
    const response = await api.post<{ data: User }>('/users', payload);
    return response.data;
  },
  update: async (id: string, updates: { password?: string; role?: 'admin' | 'member' }) => {
    const response = await api.put<{ data: User }>(`/users/${id}`, updates);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete<{ message: string }>(`/users/${id}`);
    return response.data;
  },
};

// Families API
export const familyAPI = {
  // Families the current user belongs to (any authenticated user)
  mine: async () => {
    const response = await api.get<{ data: { _id: string; name: string }[] }>('/families/mine');
    return response.data;
  },
  // Admin-only below
  getAll: async () => {
    const response = await api.get<{ data: Family[] }>('/families');
    return response.data;
  },
  create: async (payload: { name: string; members?: string[] }) => {
    const response = await api.post<{ data: Family }>('/families', payload);
    return response.data;
  },
  update: async (id: string, updates: { name?: string; members?: string[] }) => {
    const response = await api.put<{ data: Family }>(`/families/${id}`, updates);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete<{ message: string }>(`/families/${id}`);
    return response.data;
  },
};

// Meal API
export const mealAPI = {
  // Get meals with optional filters
  getAll: async (params?: { startDate?: string; endDate?: string; dateString?: string }) => {
    const response = await api.get<ApiResponse<Meal[]>>('/meals', { params });
    return response.data;
  },

  // Get single meal
  getById: async (id: string) => {
    const response = await api.get<ApiResponse<Meal>>(`/meals/${id}`);
    return response.data;
  },

  // Create new meal
  create: async (meal: Omit<Meal, '_id' | 'createdAt' | 'updatedAt'>) => {
    const response = await api.post<ApiResponse<Meal>>('/meals', meal);
    return response.data;
  },

  // Update meal
  update: async (id: string, updates: Partial<Meal>) => {
    const response = await api.put<ApiResponse<Meal>>(`/meals/${id}`, updates);
    return response.data;
  },

  // Delete meal
  delete: async (id: string) => {
    const response = await api.delete<ApiResponse<void>>(`/meals/${id}`);
    return response.data;
  },

  // Get week meals
  getWeekMeals: async (startDate: string) => {
    const response = await api.get<ApiResponse<Meal[]>>(`/meals/week/${startDate}`);
    return response.data;
  },

  // Get month meals
  getMonthMeals: async (year: number, month: number) => {
    const response = await api.get<ApiResponse<Meal[]>>(`/meals/month/${year}/${month}`);
    return response.data;
  },
};

// Database backup/restore API
export const databaseAPI = {
  exportDatabase: async (): Promise<Blob> => {
    const response = await api.get('/database/export', { responseType: 'blob' });
    return response.data;
  },

  importDatabase: async (listId: string, payload: DatabaseBackup): Promise<ApiResponse<ImportResult>> => {
    const response = await api.post<ApiResponse<ImportResult>>('/database/import', { ...payload, list: listId });
    return response.data;
  },
};

export default api;